import { Option } from '@fluentui/react-components';
import { useEffect, useRef } from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { filenameStringSchema } from '@shared/schemas';
import { ControlledField } from '@shared/components';
import { FormDialog } from '@shared/components/dialogs/FormDialog';
import { DEFAULT_OPTION_LABEL } from '@shared/i18n';
import { TSparseItem } from '../../types/folders.types';

export type TNameAndProductsDialogState = {
  isOpen: boolean;
  products: TSparseItem[];
  onSubmit: (data: { text: string; products: string[] }) => Promise<void>;
};

type TProps = {
  title: string;
  nameLabel: string;
  productsField: { label: string; multiselect?: boolean; required?: boolean };
  setState: (state: TNameAndProductsDialogState) => void;
  state?: TNameAndProductsDialogState;
};

const PRODUCTS_REQUIRED_MESSAGE = 'At least one product is required.';

const schema = yup.object({
  text: filenameStringSchema(),
  products: yup.array().of(yup.string().defined()).required(),
});
const requiredProductsSchema = schema.shape({
  products: yup.array().of(yup.string().defined()).required().min(1, PRODUCTS_REQUIRED_MESSAGE),
});
type TFormData = yup.InferType<typeof schema>;
const DEFAULT_VALUES: TFormData = { text: '', products: [] };
const DEFAULT_VALUES_OPTIONAL_PRODUCT: TFormData = { text: '', products: [''] };

const NameAndProductsDialog = ({ title, nameLabel, productsField, setState, state }: TProps) => {
  const defaultValues = productsField.required ? DEFAULT_VALUES : DEFAULT_VALUES_OPTIONAL_PRODUCT;
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TFormData>({
    resolver: yupResolver(productsField.required ? requiredProductsSchema : schema),
    defaultValues,
  });

  const refText = useRef<HTMLInputElement>();
  useEffect(() => {
    if (state?.isOpen) {
      reset(defaultValues);
      refText.current?.focus();
    }
  }, [state?.isOpen]);

  const onSubmit = (data: TFormData) =>
    state?.onSubmit({ text: data.text, products: data.products.filter((product) => product !== '') });

  return (
    <FormDialog
      isOpen={state?.isOpen || false}
      setIsOpen={(isOpen) => {
        if (state) setState({ ...state, isOpen });
      }}
      title={title}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel="Create"
      submitIcon={null}
      isSubmitting={isSubmitting}
    >
      <div className="flex flex-col gap-4">
        <ControlledField
          name="text"
          control={control}
          errors={errors}
          label={nameLabel}
          inputType="input"
          required
          ref={refText}
        />
        {state?.products.length ? (
          <ControlledField
            name="products"
            control={control}
            errors={errors}
            label={productsField.label}
            inputType="dropdown"
            getLabel={(val) => state.products.find((product) => product._id === val)?.name || ''}
            placeholder={productsField.required ? undefined : DEFAULT_OPTION_LABEL}
            multiselect={productsField.multiselect}
            required={productsField.required}
          >
            {!productsField.required && (
              <Option value="" text={DEFAULT_OPTION_LABEL}>
                {DEFAULT_OPTION_LABEL}
              </Option>
            )}
            {state.products.map((product) => (
              <Option key={product._id} value={product._id} text={product.name}>
                {product.name}
              </Option>
            ))}
          </ControlledField>
        ) : null}
      </div>
    </FormDialog>
  );
};

export default NameAndProductsDialog;
